import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Alert, Typography, Button, IconButton, Snackbar,
  useTheme, useMediaQuery, Grid, Card, Fade
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';

import { getUserData } from '../services/storageService';
import {
  submitLivestream, getMonthlySummary, getWeeklySummary,
  getUserSummary, requestAdvancePayment, getAdvancePaymentHistory,
  cancelAdvancePayment
} from '../services/livestreamService';
import { getMyCurrentCommission, getMyCommissionHistory, getLeaderCurrentCommission, getLeaderCommissionHistory } from '../services/commissionService';

import Sidebar from './common/Sidebar';

import RevenueSubmit from './RevenueSubmit';

import DashboardHeader from './dashboard/DashboardHeader';
import StatsGrid from './dashboard/StatsGrid';
import IncomeCard from './dashboard/IncomeCard';
import EarningsKPICard from './dashboard/EarningsKPICard';
import AttendanceForm from './dashboard/AttendanceForm';
import CommissionWidget from './dashboard/CommissionWidget';
import LeaderTeamWidget from './dashboard/LeaderTeamWidget';
import RecentActivity from './dashboard/RecentActivity';
import QuickActions from './dashboard/QuickActions';
import AdvancePaymentDialog from './dashboard/dialogs/AdvancePaymentDialog';
import AdvanceHistoryDialog from './dashboard/dialogs/AdvanceHistoryDialog';
import ProfileDialog from './dashboard/dialogs/ProfileDialog';
import { modernTheme } from './dashboard/theme';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com/api';

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [user, setUser] = useState(getUserData());

  // Form state
  const [date, setDate] = useState(new Date());
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [loading, setLoading] = useState(false);

  // Data state
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [userSummary, setUserSummary] = useState(null);
  const [commissionData, setCommissionData] = useState(null);
  const [commissionHistory, setCommissionHistory] = useState([]);
  const [leaderData, setLeaderData] = useState(null);

  // UI state
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || '', bankAccount: '', bankName: '',
    bankAccountHolder: '', kpiHoursPerPeriod: 160, baseSalary: 0,
  });

  // Bank info reminder
  const [showBankInfoReminder, setShowBankInfoReminder] = useState(false);
  const [bankInfoReminderOpen, setBankInfoReminderOpen] = useState(false);
  const reminderPersistent = false;

  // Advance payment
  const [advancePaymentDialogOpen, setAdvancePaymentDialogOpen] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [advancePercentage, setAdvancePercentage] = useState(10);
  const [advanceHistory, setAdvanceHistory] = useState([]);
  const [advanceHistoryDialogOpen, setAdvanceHistoryDialogOpen] = useState(false);
  const [pendingAdvance, setPendingAdvance] = useState(null);
  const [maxAdvanceAmount, setMaxAdvanceAmount] = useState(0);

  // --- Data fetching ---
  useEffect(() => {
    if (!user) { navigate('/'); return; }
    fetchUserProfile();
    fetchSummaries();
    fetchUserSummary();
    fetchAdvanceHistory();
    fetchCommissionData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  useEffect(() => {
    if (userSummary?.currentPeriod) {
      const maxAmount = userSummary.currentPeriod.earnings * 0.5;
      setMaxAdvanceAmount(maxAmount);
      setAdvanceAmount(Math.min(userSummary.currentPeriod.earnings * (advancePercentage / 100), maxAmount));
    }
  }, [userSummary, advancePercentage]);

  useEffect(() => {
    const reminderClosed = sessionStorage.getItem('bankInfoReminderClosed');
    if (reminderClosed === 'true' && !reminderPersistent) {
      setBankInfoReminderOpen(false);
      setShowBankInfoReminder(false);
    }
  }, [reminderPersistent]);

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const fetchAdvanceHistory = async () => {
    if (!user) return;
    try {
      const result = await getAdvancePaymentHistory(user.userId);
      if (result.success) {
        setAdvanceHistory(result.data);
        setPendingAdvance(result.data.find(p => p.status === 'pending') || null);
      }
    } catch (error) { console.error('Error fetching advance history:', error); }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${user.userId}` }
      });
      if (response.data.success) {
        const d = response.data.data;
        setProfileData({
          fullName: d.fullName || '', bankAccount: d.bankAccount || '',
          bankName: d.bankName || '', bankAccountHolder: d.bankAccountHolder || '',
          kpiHoursPerPeriod: 160, baseSalary: d.baseSalary || 0,
        });
        const hasBankInfo = d.bankAccount && d.bankName && d.bankAccountHolder;
        setShowBankInfoReminder(!hasBankInfo);
        if (!hasBankInfo) setBankInfoReminderOpen(true);
      }
    } catch (error) {
      showNotification('Không thể lấy thông tin cá nhân', 'error');
    }
  };

  const fetchSummaries = async () => {
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const firstDayOfMonth = new Date(year, now.getMonth(), 1);
      const firstDayOfWeek = firstDayOfMonth.getDay();
      const firstDayOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
      const weekOfMonth = Math.ceil((now.getDate() + firstDayOffset) / 7);

      const [monthlyRes, weeklyRes] = await Promise.all([
        getMonthlySummary(user.userId, year, month),
        getWeeklySummary(user.userId, year, weekOfMonth),
      ]);
      if (monthlyRes.success) setMonthlySummary(monthlyRes.data);
      if (weeklyRes.success) setWeeklySummary(weeklyRes.data);
    } catch (error) {
      showNotification('Có lỗi xảy ra khi lấy dữ liệu thống kê', 'error');
    }
  };

  const fetchUserSummary = async () => {
    try {
      const result = await getUserSummary(user.userId);
      if (result.success) setUserSummary(result.data);
    } catch (error) { console.error('Error fetching user summary:', error); }
  };

  const fetchCommissionData = async () => {
    try {
      if (user?.role === 'leader_live') {
        // Leader: fetch leader-specific data
        const [leaderRes, leaderHistoryRes] = await Promise.all([
          getLeaderCurrentCommission(), getLeaderCommissionHistory(6),
        ]);
        if (leaderRes.success) {
          setLeaderData(leaderRes.data);
          // Also set commissionData for backward compatibility with existing components
          setCommissionData(leaderRes.data.personal);
        }
        if (leaderHistoryRes.success) setCommissionHistory(leaderHistoryRes.data);
      } else {
        const [currentRes, historyRes] = await Promise.all([
          getMyCurrentCommission(), getMyCommissionHistory(6),
        ]);
        if (currentRes.success) setCommissionData(currentRes.data);
        if (historyRes.success) setCommissionHistory(historyRes.data);
      }
    } catch (error) { console.error('Error fetching commission:', error); }
  };

  // --- Handlers ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    const hoursNum = parseInt(hours) || 0;
    const minutesNum = parseInt(minutes) || 0;

    if (hoursNum < 0 || hoursNum > 24) { showNotification('Số giờ phải từ 0 đến 24', 'error'); return; }
    if (minutesNum < 0 || minutesNum >= 60) { showNotification('Số phút phải từ 0 đến 59', 'error'); return; }
    if (hoursNum === 0 && minutesNum === 0) { showNotification('Vui lòng nhập thời gian livestream', 'error'); return; }

    const totalHours = hoursNum + (minutesNum / 60);
    setLoading(true);
    try {
      const result = await submitLivestream({
        userId: user.userId,
        date: date.toISOString().split('T')[0],
        hours: Number(totalHours.toFixed(3)),
      });
      if (result.success) {
        const timeDisplay = hoursNum > 0 && minutesNum > 0
          ? `${hoursNum} giờ ${minutesNum} phút`
          : hoursNum > 0 ? `${hoursNum} giờ` : `${minutesNum} phút`;
        showNotification(`Chấm công thành công: ${timeDisplay}`);
        setHours(''); setMinutes('');

        if (monthlySummary) {
          const selectedMonth = date.getMonth() + 1;
          const currentMonth = new Date().getMonth() + 1;
          if (selectedMonth === currentMonth && date.getFullYear() === new Date().getFullYear()) {
            setMonthlySummary(prev => ({
              ...prev,
              totalHours: (prev.totalHours || 0) + totalHours,
              records: [{
                _id: result.data._id || Date.now().toString(),
                userId: user.userId, date: date.toISOString().split('T')[0],
                hours: hoursNum, minutes: minutesNum, totalHours,
                createdAt: new Date().toISOString(),
              }, ...prev.records],
            }));
          }
        }
        await Promise.all([fetchUserSummary(), fetchSummaries()]);
      } else {
        showNotification(result.error?.message || 'Lỗi khi chấm công', 'error');
      }
    } catch (error) {
      showNotification('Có lỗi xảy ra khi chấm công', 'error');
    } finally { setLoading(false); }
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`${API_URL}/auth/profile`, profileData, {
        headers: { Authorization: `Bearer ${user.userId}` }
      });
      if (response.data.success) {
        showNotification('Cập nhật thông tin cá nhân thành công', 'success');
        setProfileDialogOpen(false);
        const updatedUser = { ...user, fullName: profileData.fullName };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        const hasBankInfo = profileData.bankAccount && profileData.bankName && profileData.bankAccountHolder;
        setShowBankInfoReminder(!hasBankInfo);
        setBankInfoReminderOpen(!hasBankInfo);
      } else { showNotification('Cập nhật thông tin thất bại', 'error'); }
    } catch (error) {
      showNotification('Có lỗi xảy ra khi cập nhật thông tin', 'error');
    } finally { setLoading(false); }
  };

  const handleOpenAdvancePayment = () => {
    if (pendingAdvance) { showNotification('Bạn đã có một yêu cầu ứng tiền đang chờ xử lý', 'info'); return; }
    if (showBankInfoReminder) { showNotification('Vui lòng cập nhật thông tin ngân hàng trước khi ứng tiền', 'warning'); setBankInfoReminderOpen(true); return; }
    if (!userSummary?.currentPeriod) { showNotification('Không thể tải thông tin thu nhập kỳ này', 'error'); return; }
    setAdvancePaymentDialogOpen(true);
  };

  const handleRequestAdvancePayment = async () => {
    setLoading(true);
    try {
      const result = await requestAdvancePayment({ userId: user.userId, amount: advanceAmount });
      if (result.success) {
        showNotification('Đã gửi yêu cầu ứng tiền thành công', 'success');
        setAdvancePaymentDialogOpen(false);
        fetchAdvanceHistory();
      } else { showNotification(result.error?.message || 'Có lỗi xảy ra khi gửi yêu cầu', 'error'); }
    } catch (error) {
      showNotification('Có lỗi xảy ra khi gửi yêu cầu ứng tiền', 'error');
    } finally { setLoading(false); }
  };

  const handleCancelAdvancePayment = async (id) => {
    try {
      const result = await cancelAdvancePayment(id);
      if (result.success) { showNotification('Đã hủy yêu cầu ứng tiền thành công', 'success'); fetchAdvanceHistory(); }
      else { showNotification(result.error?.message || 'Có lỗi xảy ra khi hủy yêu cầu', 'error'); }
    } catch (error) { showNotification('Có lỗi xảy ra khi hủy yêu cầu ứng tiền', 'error'); }
  };

  const handleAdvancePercentageChange = (_, newValue) => setAdvancePercentage(newValue);

  const handleAdvanceAmountChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && userSummary?.currentPeriod) {
      const maxAmount = userSummary.currentPeriod.earnings * 0.5;
      setAdvanceAmount(Math.min(value, maxAmount));
      setAdvancePercentage(Math.min((value / userSummary.currentPeriod.earnings) * 100, 50));
    } else { setAdvanceAmount(0); setAdvancePercentage(0); }
  };

  const handleCloseBankInfoReminder = () => {
    setBankInfoReminderOpen(false);
    if (!reminderPersistent) setShowBankInfoReminder(false);
    sessionStorage.setItem('bankInfoReminderClosed', 'true');
  };

  // --- Render ---
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Sidebar user={user}>
        <Container maxWidth="xl" sx={{ py: { xs: 1, md: 3 }, px: { xs: 0, sm: 2, md: 3 }, overflowX: 'hidden' }}>
          {/* Header */}
          <DashboardHeader
            user={user} monthlySummary={monthlySummary}
            pendingAdvance={pendingAdvance}
            onOpenProfile={() => setProfileDialogOpen(true)}
            isMobile={isMobile}
          />

          {/* Stats Grid */}
          <StatsGrid
            monthlySummary={monthlySummary}
            weeklySummary={weeklySummary}
            userSummary={userSummary}
            commissionData={commissionData}
            baseSalary={profileData.baseSalary}
          />

          {/* Bank Info Reminder */}
          {showBankInfoReminder && bankInfoReminderOpen && (
            <Alert
              severity="warning"
              sx={{
                mb: 3, borderRadius: 2,
                bgcolor: '#fffbeb', border: '1px solid #fde68a',
                '& .MuiAlert-icon': { color: modernTheme.colors.warning },
              }}
              action={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button variant="contained" color="warning" size="small"
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    onClick={() => setProfileDialogOpen(true)}
                  >
                    Cập nhật ngay
                  </Button>
                  <IconButton size="small" onClick={handleCloseBankInfoReminder} sx={{ color: modernTheme.colors.warning }}>
                    <CancelIcon />
                  </IconButton>
                </Box>
              }
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Vui lòng cập nhật thông tin ngân hàng
              </Typography>
              <Typography variant="body2">
                Để nhận thanh toán và sử dụng tính năng ứng tiền, bạn cần cập nhật đầy đủ thông tin tài khoản ngân hàng.
              </Typography>
            </Alert>
          )}

          {/* Main 2-Column Layout */}
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }} alignItems="flex-start">
            {/* Left Column: Forms */}
            <Grid item xs={12} lg={7}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
                <AttendanceForm
                  date={date} onDateChange={(v) => setDate(v)}
                  hours={hours} onHoursChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || (v.length <= 2 && parseInt(v) <= 24)) setHours(v);
                  }}
                  minutes={minutes} onMinutesChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || (v.length <= 2 && parseInt(v) <= 59)) setMinutes(v);
                  }}
                  loading={loading} onSubmit={handleSubmit}
                  userSummary={userSummary}
                />

                <Fade in timeout={900}>
                  <Card sx={{
                    borderRadius: 3,
                    border: `1px solid ${modernTheme.colors.gray[200]}`,
                    boxShadow: modernTheme.shadows.md,
                    background: modernTheme.gradients.card,
                  }}>
                    <Box sx={{ p: { xs: 2, md: 3 } }}>
                      <RevenueSubmit userId={user?.userId} isMobile={isMobile} />
                    </Box>
                  </Card>
                </Fade>

              </Box>
            </Grid>

            {/* Right Column: Summary Cards */}
            <Grid item xs={12} lg={5}>
              <Box sx={{
                display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 },
                position: { lg: 'sticky' }, top: { lg: 24 },
              }}>
                <IncomeCard
                  userSummary={userSummary}
                  baseSalary={profileData.baseSalary}
                  commissionData={commissionData}
                  leaderData={leaderData}
                  userRole={user?.role}
                  pendingAdvance={pendingAdvance}
                  onOpenAdvancePayment={handleOpenAdvancePayment}
                  onOpenAdvanceHistory={() => setAdvanceHistoryDialogOpen(true)}
                />

                <EarningsKPICard
                  userSummary={userSummary}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Leader Team Widget - only for leader_live */}
          {user?.role === 'leader_live' && leaderData && (
            <Box sx={{ mb: 3 }}>
              <LeaderTeamWidget
                leaderData={leaderData}
                userSummary={userSummary}
                isMobile={isMobile}
              />
            </Box>
          )}

          {/* Commission Widget - full width */}
          <Box sx={{ mb: 3 }}>
            <CommissionWidget
              commissionData={commissionData}
              commissionHistory={commissionHistory}
              isMobile={isMobile}
              userRole={user?.role}
              leaderData={leaderData}
            />
          </Box>

          {/* Recent Activity */}
          <RecentActivity
            monthlySummary={monthlySummary}
            userSummary={userSummary}
            loading={loading}
            onViewAll={() => navigate('/dashboard/history')}
            isMobile={isMobile}
          />

          {/* Dialogs */}
          <AdvancePaymentDialog
            open={advancePaymentDialogOpen}
            onClose={() => setAdvancePaymentDialogOpen(false)}
            loading={loading}
            userSummary={userSummary}
            advancePercentage={advancePercentage}
            onPercentageChange={handleAdvancePercentageChange}
            advanceAmount={advanceAmount}
            onAmountChange={handleAdvanceAmountChange}
            maxAdvanceAmount={maxAdvanceAmount}
            onSubmit={handleRequestAdvancePayment}
          />

          <AdvanceHistoryDialog
            open={advanceHistoryDialogOpen}
            onClose={() => setAdvanceHistoryDialogOpen(false)}
            advanceHistory={advanceHistory}
            onCancel={handleCancelAdvancePayment}
          />

          <ProfileDialog
            open={profileDialogOpen}
            onClose={() => setProfileDialogOpen(false)}
            loading={loading}
            profileData={profileData}
            onChange={(e) => setProfileData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
            onSubmit={handleUpdateProfile}
          />

          <Snackbar
            open={notification.open}
            autoHideDuration={6000}
            onClose={() => setNotification(prev => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert
              onClose={() => setNotification(prev => ({ ...prev, open: false }))}
              severity={notification.severity}
              sx={{ borderRadius: 2, boxShadow: modernTheme.shadows.md }}
            >
              {notification.message}
            </Alert>
          </Snackbar>

          {/* Quick Actions */}
          <QuickActions
            isMobile={isMobile}
            onOpenAdvancePayment={handleOpenAdvancePayment}
            onOpenProfile={() => setProfileDialogOpen(true)}
          />
        </Container>
      </Sidebar>
    </LocalizationProvider>
  );
};

export default Dashboard;
